import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatDateForApi, formatDatePretty, getRiskBadgeClass, getRiskLabel } from '../utils/helpers';
import { ChevronLeft, Building2, User, Calendar as CalendarIcon, Plus, Printer, MessageSquare, Camera } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuditDetail = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const { isAuditor } = useAuth();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState(null);
  const [priority, setPriority] = useState('medium');
  const [creating, setCreating] = useState(false);

  const fetchAudit = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/audits/${auditId}`);
      setAudit(response.data);
    } catch (error) {
      toast.error('Failed to load audit');
    } finally {
      setLoading(false);
    }
  }, [auditId]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const handleCreateAction = async () => {
    if (!description || !assignedTo || !dueDate) {
      toast.error('Please fill all required fields');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/corrective-actions`, {
        audit_id: auditId,
        question_id: selectedQuestion.question_id,
        description,
        assigned_to: assignedTo,
        due_date: formatDateForApi(dueDate),
        priority
      });
      toast.success('Corrective action created');
      setShowActionDialog(false);
      setDescription('');
      setAssignedTo('');
      setDueDate(null);
      setPriority('medium');
      setSelectedQuestion(null);
    } catch (error) {
      toast.error('Failed to create action');
    } finally {
      setCreating(false);
    }
  };

  const openActionDialog = (response) => {
    setSelectedQuestion(response);
    setShowActionDialog(true);
  };

  const groupResponsesByCategory = (responses) => {
    const grouped = {};
    responses.forEach(r => {
      if (!grouped[r.category]) grouped[r.category] = [];
      grouped[r.category].push(r);
    });
    return grouped;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;
  }

  if (!audit) {
    return <div className="text-center py-12"><p className="text-slate-500">Audit not found</p><Button onClick={() => navigate('/audits')} className="mt-4">Go to Audits</Button></div>;
  }

  const groupedResponses = groupResponsesByCategory(audit.responses || []);
  const categories = Object.keys(groupedResponses);

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="audit-detail-page">
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/audits')} data-testid="back-btn"><ChevronLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900">Audit Report</h1>
            <p className="text-slate-500 mono text-sm">ID: {audit.id.slice(0, 8)}</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => window.print()} className="flex items-center gap-2" data-testid="print-btn"><Printer className="w-4 h-4" />Print Report</Button>
      </div>

      <Card data-testid="summary-card">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <SummaryRow icon={Building2} label="Site" value={audit.site_name} subvalue={audit.site_location} />
              <SummaryRow icon={User} label="Auditor" value={audit.auditor_name} />
              <SummaryRow icon={CalendarIcon} label="Date" value={formatDate(audit.created_at)} />
            </div>
            <ScoreDisplay score={audit.score_percentage} riskLevel={audit.risk_level} obtained={audit.obtained_score} total={audit.total_max_score} />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="breakdown-card">
        <CardHeader><CardTitle className="text-lg font-['Chivo']">Score Breakdown by Category</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {categories.map(cat => <CategoryBreakdown key={cat} category={cat} responses={groupedResponses[cat]} />)}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg font-['Chivo']">Detailed Responses</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Accordion type="multiple" defaultValue={categories} className="divide-y">
            {categories.map(cat => (
              <AccordionItem key={cat} value={cat} className="border-0">
                <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid={`detail-category-${cat}`}>
                  <span className="font-semibold text-slate-900">{cat}</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <div className="space-y-4">
                    {groupedResponses[cat].map((resp, idx) => (
                      <ResponseCard key={resp.question_id} response={resp} index={idx} isAuditor={isAuditor()} onAddAction={openActionDialog} />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Corrective Action</DialogTitle>
            <DialogDescription>{selectedQuestion?.question}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="form-label">Description *</Label><Textarea placeholder="Describe the corrective action required..." value={description} onChange={(e) => setDescription(e.target.value)} rows={3} data-testid="action-description-input" /></div>
            <div><Label className="form-label">Assigned To *</Label><Input placeholder="Name of responsible person" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} data-testid="action-assigned-input" /></div>
            <div>
              <Label className="form-label">Due Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="action-date-btn">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? formatDatePretty(dueDate) : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={dueDate} onSelect={setDueDate} initialFocus /></PopoverContent>
              </Popover>
            </div>
            <div><Label className="form-label">Priority</Label>
              <Select value={priority} onValueChange={setPriority}><SelectTrigger data-testid="action-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowActionDialog(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleCreateAction} disabled={creating} data-testid="create-action-submit-btn">{creating ? 'Creating...' : 'Create Action'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function SummaryRow({ icon: Icon, label, value, subvalue }) {
  return (
    <div className="flex items-center gap-3">
      <div className="p-2 bg-slate-100 rounded-lg"><Icon className="w-5 h-5 text-slate-600" /></div>
      <div><p className="text-sm text-slate-500">{label}</p><p className="font-semibold text-slate-900">{value}</p>{subvalue && <p className="text-sm text-slate-500">{subvalue}</p>}</div>
    </div>
  );
}

function ScoreDisplay({ score, riskLevel, obtained, total }) {
  return (
    <div className="flex flex-col items-center justify-center p-6 bg-slate-50 rounded-xl">
      <p className="text-sm font-medium text-slate-500 mb-2">Overall Score</p>
      <p className="text-5xl font-bold font-['Chivo'] text-slate-900">{score}%</p>
      <Badge className={`${getRiskBadgeClass(riskLevel)} border-0 mt-3 text-sm px-4 py-1`}>{getRiskLabel(riskLevel)}</Badge>
      <p className="text-sm text-slate-500 mt-3">{obtained} / {total} points</p>
    </div>
  );
}

function CategoryBreakdown({ category, responses }) {
  // Exclude NA items from score calculation
  const applicableResponses = responses.filter(r => !r.is_na);
  const maxScore = applicableResponses.reduce((sum, r) => sum + (r.max_score || 0), 0);
  const obtainedScore = applicableResponses.reduce((sum, r) => sum + (r.obtained_mark || 0), 0);
  const naCount = responses.filter(r => r.is_na).length;
  const percentage = maxScore > 0 ? Math.round((obtainedScore / maxScore) * 100) : 0;
  let colorClass = 'bg-red-100 text-red-700';
  if (percentage >= 90) colorClass = 'bg-emerald-100 text-emerald-700';
  else if (percentage >= 80) colorClass = 'bg-yellow-100 text-yellow-700';
  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
      <span className="font-medium text-slate-700">{category}</span>
      <div className="flex items-center gap-4">
        <span className="text-sm text-slate-500">{obtainedScore} / {maxScore}</span>
        {naCount > 0 && <Badge className="bg-gray-100 text-gray-600 text-xs">{naCount} N/A</Badge>}
        <Badge className={colorClass}>{percentage}%</Badge>
      </div>
    </div>
  );
}

function ResponseCard({ response, index, isAuditor, onAddAction }) {
  const isNA = response.is_na;
  const isFailed = !isNA && response.obtained_mark < (response.max_score || 0);
  const bgClass = isNA ? 'bg-gray-50 border-gray-100 opacity-60' : isFailed ? 'bg-red-50 border-red-100' : 'bg-white border-slate-100';
  return (
    <div className={`p-4 rounded-lg border ${bgClass}`} data-testid={`response-${response.question_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-slate-400">{index + 1}.</span>
            {isNA && <Badge className="bg-gray-200 text-gray-600 text-xs">N/A</Badge>}
            <Badge className={`${getRiskBadgeClass(response.risk)} text-xs`}>{response.risk}</Badge>
          </div>
          <p className={`text-sm ${isNA ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{response.question}</p>
          {response.comments && (
            <div className="mt-3 flex items-start gap-2 text-sm text-slate-600 bg-white p-2 rounded border">
              <MessageSquare className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{response.comments}</span>
            </div>
          )}
          {response.photos && response.photos.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-500">Attached Photos ({response.photos.length})</span>
              </div>
              <div className="flex gap-3 flex-wrap">
                {response.photos.map((photo, pi) => (
                  <a 
                    key={pi} 
                    href={`${process.env.REACT_APP_BACKEND_URL}${photo}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${photo}`} 
                      alt={`Evidence ${pi + 1}`} 
                      className="w-24 h-24 object-cover rounded-lg border border-slate-200 hover:border-blue-400 hover:shadow-md transition-all"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          {isNA ? (
            <p className="text-lg font-bold text-gray-400">N/A</p>
          ) : (
            <p className="text-lg font-bold text-slate-900">{response.obtained_mark} / {response.max_score || 0}</p>
          )}
          {isFailed && isAuditor && <Button variant="ghost" size="sm" className="mt-2 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => onAddAction(response)} data-testid={`create-action-${response.question_id}`}><Plus className="w-4 h-4 mr-1" />Add Action</Button>}
        </div>
      </div>
    </div>
  );
}

export default AuditDetail;
